import axios from "axios";

import type { ScheduledQuestionnaireListItemDto } from "@/generated/model/scheduledQuestionnaireListItemDto";

export type { SortDir } from "@/lib/category-display";

/** The four Checklist Manager tabs. History is the default (AC). */
export type ChecklistManagerTab =
  | "history"
  | "due-soon"
  | "failed"
  | "templates";

/** Sort columns supported server-side (must match the API allow-list). */
export type ChecklistSortColumn =
  | "name"
  | "category"
  | "frequency"
  | "scheduledDate"
  | "due"
  | "completedAt"
  | "score"
  | "progress"
  | "status";

/**
 * Status values that make up the History tab: everything that has reached an end
 * state, whether completed on time, completed late, or missed. Missed checklists
 * are included so an acknowledged one still has somewhere to live (NAU-71) —
 * previously they appeared in no tab at all unless they carried a failed check.
 */
export const HISTORY_STATUSES = "COMPLETED,COMPLETED_LATE,EXPIRED";
/** Not-yet-completed statuses for the Due soon tab. */
export const DUE_SOON_STATUSES = "PENDING,STARTED";

/** Forward horizon (days) for the Due soon tab — mirrors the server default. */
export const DUE_SOON_HORIZON_DAYS = 7;

export const PAGE_SIZE = 10;

/** Resolve the primary category object for a row, falling back to the first. */
export function primaryCategory(item: ScheduledQuestionnaireListItemDto) {
  const categories = item.config?.categories ?? [];
  const primaryId = item.config?.primaryCategoryId;
  return categories.find((c) => c.id === primaryId) ?? categories[0];
}

/** Progress percentage (answered / total questions), clamped 0-100. */
export function progressPercent(
  item: ScheduledQuestionnaireListItemDto,
): number {
  const total = item.template?.questionCount ?? 0;
  if (total <= 0) return 0;
  return Math.min(100, Math.round((item.answeredCount / total) * 100));
}

/** Human-friendly label for a config frequency enum value. */
export function frequencyLabel(frequency?: string): string {
  switch (frequency) {
    case "ONE_TIME":
      return "One-time";
    case "DAILY":
      return "Daily";
    case "WEEKLY":
      return "Weekly";
    case "MONTHLY":
      return "Monthly";
    default:
      return "—";
  }
}

export const ACKNOWLEDGE_GENERIC_ERROR =
  "Couldn't acknowledge these checks. Please try again.";

/** Server message for a checklist whose failed checks are already cleared. */
const ALREADY_ACKNOWLEDGED = "Checklist is already acknowledged";

/**
 * Turn an acknowledge failure into copy the user can act on. The server sends a
 * specific reason (no failed checks, already acknowledged, wrong role) and the
 * blanket "please try again" hid all of them.
 *
 * `alreadyAcknowledged` means the checklist was cleared by someone else or by
 * follow-up coverage: nothing is wrong, the list is just stale, so callers treat
 * it as success and refresh.
 */
export function acknowledgeErrorMessage(err: unknown): {
  message: string;
  alreadyAcknowledged: boolean;
} {
  if (!axios.isAxiosError(err) || !err.response) {
    return { message: ACKNOWLEDGE_GENERIC_ERROR, alreadyAcknowledged: false };
  }

  const { status, data } = err.response;
  const serverMessage = typeof data?.message === "string" ? data.message : "";

  if (status === 400 && serverMessage === ALREADY_ACKNOWLEDGED) {
    return {
      message: "These checks were already acknowledged.",
      alreadyAcknowledged: true,
    };
  }
  if (status === 400 && serverMessage) {
    return { message: serverMessage, alreadyAcknowledged: false };
  }
  if (status === 403) {
    return {
      message:
        "Only a nursery manager or group admin can acknowledge failed checks.",
      alreadyAcknowledged: false,
    };
  }
  if (status === 404) {
    return {
      message: "This checklist no longer exists.",
      alreadyAcknowledged: false,
    };
  }

  return { message: ACKNOWLEDGE_GENERIC_ERROR, alreadyAcknowledged: false };
}

/** Display status → Subframe Badge variant. */
export function statusBadge(status: string): {
  label: string;
  variant: "success" | "warning" | "error" | "neutral" | "brand";
} {
  switch (status) {
    case "COMPLETED":
      return { label: "Completed", variant: "success" };
    case "COMPLETED_LATE":
      return { label: "Completed late", variant: "warning" };
    case "EXPIRED":
      return { label: "Missed", variant: "error" };
    case "STARTED":
      return { label: "In progress", variant: "brand" };
    case "SKIPPED":
      return { label: "Skipped", variant: "neutral" };
    case "CANCELLED":
      return { label: "Cancelled", variant: "neutral" };
    default:
      return { label: "Upcoming", variant: "neutral" };
  }
}
